import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { queryClient } from "..";
import { deliveryApi } from "../api/delivery-api";
import { scheduleApi } from "../api/schedule-api";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
    addCachedUpdateDeliveries,
    addControlDeliveriesAction,
    clearControlDeliveriesAction,
    clearDeliveryPageAction,
    clearDisplayDeliveriesAction,
    clearSelectedDisplayDeliveryIdAction,
    deliveryState,
    initControlDeliveriesAction,
} from "../app/reducers/delivery-slice";
import { endGlobalLoadingAction, startGlobalLoadingAction, } from "../app/reducers/loading-slice";
import { clearPreviewAction, clearUpdateScheduleAction, scheduleState, } from "../app/reducers/schedule-slice";
import { ScheduleCreateBoardInfo } from "../components/board/ScheduleCreateBoardInfo";
import { ControlDeliveryDrawer } from "../components/drawer/ControlDeliveryDrawer";
import { DisplayDeliveryDrawer } from "../components/drawer/DisplayDeliveryDrawer";
import ScheduleCreateMap from "../components/map/ScheduleCreateMap";
import { DeliveryQueryKey } from "../query/delivery-query";
import { ControlDelivery } from "../types/delivery/control-delivery";
import { DeliveryGroupType } from "../types/schedule/delivery-group-type";
import { DeliverySegment } from "../types/schedule/delivery-segment";
import { CreateSchedulePayload, UpdateSchedulePayload, } from "../types/schedule/schedule-payload";
import useDidMountEffect from "../utils/custom-hook/use-did-mount-effect";
import { emptySplitDelivery } from "../utils/delivery/delivery";
import { useNavigate } from "react-router";
import { Pathname } from "../types/pathname";
import { Delivery } from "../types/delivery/delivery";

interface Props {
    selectedWorkerId?: string;
    setSelectedWorkerId: Dispatch<SetStateAction<string | undefined>>;
}

export const ScheduleFormDeliveryView: React.FC<Props> = (
    {
        selectedWorkerId,
        setSelectedWorkerId,
    }
) => {
    const dispatch = useAppDispatch();
    const { controlDeliveries, displayDeliveries, cachedUpdateDeliveries } =
        useAppSelector(deliveryState);
    const { dateToWork, updateSchedule } = useAppSelector(scheduleState);

    const [isInitUpdate, setInitUpdate] = useState(false);

    const history = useNavigate();

    const handleCreateScheduleButtonClick = async () => {
        const nonSplitDeliveries = controlDeliveries.filter(delivery => delivery.type !== DeliveryGroupType.SPLIT);
        if (nonSplitDeliveries.length === 0) {
            toast.error("물류를 선택해주세요.");
            return;
        }

        const payload: CreateSchedulePayload = {
            workerId: selectedWorkerId,
            dateToWork,
            deliverySegments: controlDeliveries.map((controlDelivery) => {
                return {
                    id: controlDelivery.delivery.id,
                    type: controlDelivery.type,
                };
            }),
        };

        try {
            await scheduleApi.create(payload);
            toast.success("스케줄이 생성되었습니다.");
            clearState();
        } catch (error) {
            console.log(error);
        }
    };

    const handleUpdateScheduleButtonClick = async () => {
        const nonSplitDeliveries = controlDeliveries.filter(delivery => delivery.type !== DeliveryGroupType.SPLIT);
        if (nonSplitDeliveries.length === 0) {
            toast.error("물류를 선택해주세요.");
            return;
        }

        if (!updateSchedule) {
            return;
        }

        const payload: UpdateSchedulePayload = {
            workerId: selectedWorkerId,
            dateToWork,
            deliverySegments: controlDeliveries.map((controlDelivery) => {
                return {
                    id: controlDelivery.delivery.id,
                    type: controlDelivery.type,
                };
            }),
        };

        try {
            await scheduleApi.update(updateSchedule.id, payload);
            toast.success("스케줄이 수정되었습니다");
            history(Pathname.ScheduleCheck);
        } catch (error) {
            console.log(error);
        }
    };

    const getRecommendDeliveries = async () => {
        if (!selectedWorkerId) {
            return;
        }

        try {
            const response = await deliveryApi.recommand(selectedWorkerId, {
                specificDate: dateToWork,
            });

            const recommendDeliveryIds = response.data.ids;
            const recommendDeliveries = recommendDeliveryIds
                .map((recommendDeliveryId) => {
                    const recommendDelivery = findRecommendDelivery(recommendDeliveryId);
                    if (recommendDelivery) {
                        return {
                            key: recommendDelivery.id + DeliveryGroupType.PickUp,
                            delivery: recommendDelivery,
                            type: DeliveryGroupType.PickUp,
                        };
                    }
                })
                .filter((delivery) => delivery) as ControlDelivery[];

            if (recommendDeliveries.length === 0) {
                dispatch(clearControlDeliveriesAction());
                return;
            }
            toast.success("추천 물류가 반영되었습니다. 스케줄을 확인해주세요.");

            dispatch(
                addControlDeliveriesAction({
                    deliveries: recommendDeliveries,
                })
            );
        } catch (error) {
            console.log(error);
        }
    };

    const initUpdateSchedule = () => {
        if (updateSchedule && displayDeliveries.length > 0 && !isInitUpdate) {
            const deliveriesTemp: ControlDelivery[] = [];
            updateSchedule.deliverySegments.forEach((deliverySegment) => {
                const delivery = findInitUpdateDelivery(deliverySegment);
                if (delivery) {
                    deliveriesTemp.push({
                        key: delivery.id + deliverySegment.type,
                        delivery,
                        type: deliverySegment.type,
                    });
                    dispatch(
                        addCachedUpdateDeliveries({
                            deliveries: [delivery],
                        }
                        )
                    );
                } else {
                    deliveriesTemp.push({
                        key: deliverySegment.id + deliverySegment.type,
                        delivery: emptySplitDelivery,
                        type: deliverySegment.type,
                    });
                }
            });

            //TODO!
            if (
                deliveriesTemp.every(
                    (delivery) => delivery.delivery.id === emptySplitDelivery.id
                )
            ) {
                return;
            }

            setInitUpdate(true);
            dispatch(
                initControlDeliveriesAction({
                    deliveries: deliveriesTemp,
                })
            );
        }
    };

    const findInitUpdateDelivery = (deliverySegment: DeliverySegment) => {
        const deliveriesInDisplayDeliveries: Delivery[] = displayDeliveries.flatMap(
            (displayDelivery) => {
                return displayDelivery.deliveries.map((delivery) => delivery);
            }
        )

        const deliveries = deliveriesInDisplayDeliveries.concat(cachedUpdateDeliveries);

        return deliveries.find((delivery) => {
            return delivery.id === deliverySegment.id;
        });
    };

    const findRecommendDelivery = (recommendDeliveryId: string) => {
        const deliveriesInDisplayDeliveries = displayDeliveries.flatMap(
            (displayDelivery) => {
                return displayDelivery.deliveries.map((delivery) => delivery);
            }
        );
        return deliveriesInDisplayDeliveries.find((delivery) => {
            return delivery.id === recommendDeliveryId;
        });
    };

    const clearState = () => {
        setSelectedWorkerId(undefined);
        dispatch(clearControlDeliveriesAction());
        dispatch(clearSelectedDisplayDeliveryIdAction());
        dispatch(clearPreviewAction());
        dispatch(clearUpdateScheduleAction());
        dispatch(clearDisplayDeliveriesAction());
        dispatch(clearDeliveryPageAction());
        queryClient.invalidateQueries([DeliveryQueryKey.PageAll]);
    };

    useDidMountEffect(() => {
        if (!selectedWorkerId) {
            return;
        }

        if (dateToWork === "") {
            return;
        }

        if (updateSchedule) {
            return;
        }

        if (controlDeliveries.length > 0) {
            return;
        }

        getRecommendDeliveries();
    }, [dateToWork, selectedWorkerId]);

    useEffect(() => {
        setTimeout(() => {
            dispatch(endGlobalLoadingAction());
        }, 500);
        if (displayDeliveries.length === 0) {
            return;
        }
        initUpdateSchedule();
    }, [displayDeliveries]);

    useEffect(() => {
        dispatch(startGlobalLoadingAction());
        return () => {
            clearState();
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.stopPropagation();
                if (controlDeliveries.length === 0) {
                    return;
                }

                if (updateSchedule) {
                    if (window.confirm('스케줄을 수정하시겠어요?')) {
                        handleUpdateScheduleButtonClick();
                    }
                } else {
                    if (window.confirm('스케줄을 등록하시겠어요?')) {
                        handleCreateScheduleButtonClick();
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [controlDeliveries, handleCreateScheduleButtonClick, handleUpdateScheduleButtonClick, updateSchedule]);

    return (
        <ScheduleFormClassContainer>
            <ScheduleCreateMapContainer>
                <DrawerContainer>
                    <DrawerColumnContainer>
                        <DisplayDeliveryDrawer />
                        <ControlDeliveryDrawer />
                    </DrawerColumnContainer>
                    <ScheduleCreateBoardInfo
                        handleCreateScheduleButtonClick={handleCreateScheduleButtonClick}
                        handleUpdateScheduleButtonClick={handleUpdateScheduleButtonClick}
                    />
                </DrawerContainer>
                <ScheduleCreateMap />
            </ScheduleCreateMapContainer>
        </ScheduleFormClassContainer>
    );
};

const ScheduleFormClassContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 88%;
  width: 100%;
`;

const ScheduleCreateMapContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const DrawerContainer = styled.div`
  display: flex;
  position: absolute;
  background: transparent;
  height: 100%;
`;

const DrawerColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
`;
